import {
  api,
  CASE_METADATA_TAG,
  CASE_STATUS_TAG,
  CaseDocumentStatus,
  isEndpointLockError,
  useLazyGetCaseQuery,
  useLazyPostCaseStartPaginationQuery,
} from '@/api';
import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';
import {
  selectCaseDocumentsList,
  selectCaseIdData,
  selectCaseMetadata,
  selectCaseStatus,
  useAppSelector,
} from '@/store';
import { TriangleIcon } from '@repo/atomic-components';
import React, { useMemo } from 'react';
import { GreenButton } from '@/ui/components/GreenButton';
import { useCaseStatusPolling } from '../hooks';
import { CaseStatus } from '../utils';
import { CaseDocumentGdprStatus } from '@/utils';

export const StartAIProcessingButton: React.FC = () => {
  const caseId = useAppSelector(selectCaseIdData);
  const caseDocumentsList = useAppSelector(selectCaseDocumentsList);
  const caseMetadata = useAppSelector(selectCaseMetadata);
  const caseStatus = useAppSelector(selectCaseStatus);
  const isCancelled =
    caseStatus?.status === CaseStatus.AI_TIMELINE_CANCELLED ||
    caseStatus?.status === CaseStatus.AI_PAGINATION_CANCELLED;
  const [triggerRefetchCase] = useLazyGetCaseQuery();
  const isStartPaginationAiProcessing = useAccessControl(AccessControlAction.START_PAGINATION_AI_PROCESSING);

  const [triggerPostCaseStartPagination] = useLazyPostCaseStartPaginationQuery();

  const { isPolling, startPolling, stopPolling } = useCaseStatusPolling(caseId);

  const handleStartButtonClick = () => {
    if (caseId) {
      stopPolling();
      triggerPostCaseStartPagination({ caseId: caseId })
        .unwrap()
        .then(() => {
          api.util.invalidateTags([
            { type: CASE_STATUS_TAG, id: caseId },
            { type: CASE_METADATA_TAG, id: caseId },
          ]);
          triggerRefetchCase({ caseId });
        })
        .catch((error) => {
          // Nothing started, and the polling below would keep the button disabled after the clone has finished
          if (isEndpointLockError(error)) stopPolling();
        });
      startPolling();
    }
  };

  const isAllegationDocumentsUploaded = useMemo(() => {
    const allegationDocuments = caseMetadata?.allegationDocuments;
    return Array.isArray(allegationDocuments) && allegationDocuments.length > 0;
  }, [caseMetadata]);

  // All files must NOT be in 'Uploading' status AND have a provider assigned
  const areAllDocumentsUploadedWithProvider = useMemo(() => {
    if (caseDocumentsList.length === 0) return false;
    return caseDocumentsList.every(
      (document) =>
        document.status !== CaseDocumentStatus.UPLOADING &&
        document.providerData &&
        document.providerData.providerId &&
        document.providerData.providerName &&
        document.providerData.providerType,
    );
  }, [caseDocumentsList]);

  // Determine if documents are ready based on product type
  const isAllDocumentsReady = useMemo(() => {
    return areAllDocumentsUploadedWithProvider;
  }, [areAllDocumentsUploadedWithProvider]);

  const hasDocumentsWithoutProvider = useMemo(() => {
    return caseDocumentsList.some((document) => !document.providerData || !document.providerData.providerId);
  }, [caseDocumentsList]);

  const hasOutstandingGdprChecks = useMemo(() => {
    return caseDocumentsList.some(
      (document) =>
        document.status === CaseDocumentGdprStatus.READY_FOR_GDPR_CHECK ||
        document.status === CaseDocumentGdprStatus.GDPR_CHECK_IN_PROGRESS,
    );
  }, [caseDocumentsList]);

  if (!isStartPaginationAiProcessing) {
    return null;
  }

  const disableButton =
    !isAllDocumentsReady || !isAllegationDocumentsUploaded || !caseStatus?.canSendForAi || isPolling;

  const getDisabledTooltip = (): string | undefined => {
    if (isCancelled || !disableButton) return undefined;
    if (hasDocumentsWithoutProvider) {
      return 'Assign a provider to every uploaded document to begin AI processing.';
    }
    if (hasOutstandingGdprChecks) {
      return 'Complete GDPR check on all documents to begin AI processing.';
    }
    return undefined;
  };

  const tooltip = getDisabledTooltip();

  return (
    <span title={tooltip}>
      <GreenButton
        className="!h-8 min-w-25 whitespace-nowrap"
        onClick={handleStartButtonClick}
        disabled={isCancelled ? false : disableButton}
      >
        <TriangleIcon style={{ width: '12px', height: '12px' }} />
        <span className="ml-2">Start AI Processing</span>
      </GreenButton>
    </span>
  );
};
